import { api, ApiError } from '@/lib/api';

/**
 * The two calls behind "I forgot my password".
 *
 * `auth: false` on both. They are the only unauthenticated POSTs outside
 * `/api/public/*`, and the flag matters for the same reason it does there: a 401
 * on an authenticated request makes the store log out and hard-redirect to
 * /login, which on this flow would bounce somebody mid-reset back to the page
 * they already told us they cannot get past. There is no session here to lose.
 */

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post<{ requested: true }>(
    '/api/auth/password/forgot',
    { email },
    { auth: false },
  );
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api.post<{ reset: true }>(
    '/api/auth/password/reset',
    { token, newPassword },
    { auth: false },
  );
}

/**
 * Which of the three ways a reset link can be dead, if that is what went wrong.
 *
 * The server sends the reason in `details` rather than only in the message, so
 * the page can say "ask for a new link" for an expired token and "that link has
 * already been used" for a spent one — two situations with genuinely different
 * next steps, which one generic error string would flatten into "it didn't
 * work".
 *
 * Returns null for anything else (a network failure, a rate limit, a weak
 * password), which the caller renders through `translateApiError` as usual.
 */
export type ResetRejection = 'invalid' | 'expired' | 'used';

const REJECTIONS: ReadonlySet<string> = new Set(['invalid', 'expired', 'used']);

export function resetRejectionOf(err: unknown): ResetRejection | null {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_ERROR') return null;
  const reason = (err.details as { reason?: unknown } | null | undefined)?.reason;
  return typeof reason === 'string' && REJECTIONS.has(reason)
    ? (reason as ResetRejection)
    : null;
}
